import React, { useEffect, useMemo, useState } from 'react';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ExampleEntry,
  getAudioUrl,
  getVocabGraph,
  getWordContext,
  LexiconResponse,
  lookupWord,
  VocabGraphNode,
  VocabGraphResponse,
  WordContextItem,
} from '../utils/api';
import { escapeRegExp } from '../utils/text';

const SCENE_LABELS: Record<string, { label: string; color: string }> = {
  daily: { label: 'DAILY LIFE', color: '#2563eb' },
  business: { label: 'BUSINESS', color: '#7c3aed' },
  academic: { label: 'ACADEMIC', color: '#0891b2' },
  legal: { label: 'LEGAL', color: '#dc2626' },
  political: { label: 'POLITICAL', color: '#ef4444' },
  artistic: { label: 'ARTISTIC', color: '#d97706' },
  slang: { label: 'SLANG', color: '#7c3aed' },
  ironic: { label: 'IRONIC', color: '#ef4444' },
  regional: { label: 'REGIONAL', color: '#d97706' },
};

const NODE_COLORS: Record<string, string> = {
  synonym: '#22c55e',
  antonym: '#ef4444',
  derivative: '#2563eb',
  collocate: '#8b5cf6',
  hypernym: '#f97316',
  hyponym: '#fb923c',
};

function normalizeContexts(payload: ReturnType<typeof getWordContext> extends Promise<infer T> ? T : never): WordContextItem[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  return payload.contexts || payload.items || [];
}

function highlightWord(text: string, word: string) {
  const parts = text.split(new RegExp(`(${escapeRegExp(word)})`, 'ig'));
  return parts.map((part, index) =>
    part.toLowerCase() === word.toLowerCase() ? (
      <Box component="span" key={`${part}-${index}`} sx={{ textDecoration: 'underline', textDecorationColor: '#0891b2', fontWeight: 700 }}>
        {part}
      </Box>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );
}

function fallbackExamples(word: string): ExampleEntry[] {
  return [
    { sentence: `The author uses ${word} to capture a feeling that cannot be translated directly.`, source: 'Modern Essay Review' },
    { sentence: `Each repetition of ${word} in the poem reveals a slightly different shade of meaning.`, source: 'Contemporary Poetry Notes' },
  ];
}

export default function WordDetailPage() {
  const { text = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const lang = (new URLSearchParams(location.search).get('lang') || 'en').toLowerCase();
  const decodedWord = decodeURIComponent(text);

  const [lexicon, setLexicon] = useState<LexiconResponse | null>(null);
  const [contexts, setContexts] = useState<WordContextItem[]>([]);
  const [graph, setGraph] = useState<VocabGraphResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    Promise.allSettled([
      lookupWord(decodedWord, lang),
      getWordContext(decodedWord, lang),
      getVocabGraph(decodedWord, lang),
    ])
      .then(([lexiconResult, contextResult, graphResult]) => {
        if (!active) {
          return;
        }
        if (lexiconResult.status === 'fulfilled') {
          setLexicon(lexiconResult.value);
        }
        if (contextResult.status === 'fulfilled') {
          setContexts(normalizeContexts(contextResult.value));
        }
        if (graphResult.status === 'fulfilled') {
          setGraph(graphResult.value);
        }

        if (
          lexiconResult.status === 'rejected' &&
          contextResult.status === 'rejected' &&
          graphResult.status === 'rejected'
        ) {
          setError(lexiconResult.reason?.message || 'Unable to load word details.');
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [decodedWord, lang]);

  const definitions = lexicon?.definition || lexicon?.definitions || [];
  const primaryDefinition = definitions[0];
  const translations = lexicon?.translation || [];
  const examples = lexicon?.examples?.length ? lexicon.examples : fallbackExamples(decodedWord);
  const graphNodes = useMemo(() => graph?.nodes || [], [graph]);
  const relatedConcepts = graphNodes.filter((node) => ['synonym', 'hypernym', 'hyponym'].includes(node.type)).slice(0, 6);
  const displayedContexts = useMemo(() => {
    const defaults: WordContextItem[] = [
      { scene: 'daily', example: `${decodedWord} changes how the message feels in casual conversation.` },
      { scene: 'business', example: `In meetings, ${decodedWord} signals a more careful strategic tone.` },
      { scene: 'academic', example: `Researchers often discuss ${decodedWord} when comparing interpretations.` },
      { scene: 'legal', example: `Legal wording depends on the ${decodedWord} of every clause.` },
      { scene: 'political', example: `A candidate may choose ${decodedWord} to shift the public framing.` },
      { scene: 'artistic', example: `Artists rely on ${decodedWord} to guide emotion and symbolism.` },
    ];

    if (contexts.length === 0) {
      return defaults;
    }

    const padded = [...contexts];
    while (padded.length < 6) {
      padded.push(defaults[padded.length]);
    }
    return padded.slice(0, 6);
  }, [contexts, decodedWord]);

  const graphLayout = useMemo(() => {
    const centerX = 180;
    const centerY = 150;
    const radius = 108;
    return graphNodes.slice(0, 8).map((node, index, array) => {
      const angle = (Math.PI * 2 * index) / Math.max(array.length, 1) - Math.PI / 2;
      return {
        ...node,
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      };
    });
  }, [graphNodes]);

  const handlePlayAudio = () => {
    const audio = new Audio(getAudioUrl(decodedWord));
    audio.play().catch(() => undefined);
  };

  return (
    <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="flex-start">
      <Stack spacing={3} sx={{ flex: 1.8, width: '100%' }}>
        {loading ? (
          <Card sx={{ borderRadius: 3 }}><CardContent sx={{ py: 8 }}><Stack alignItems="center"><CircularProgress /></Stack></CardContent></Card>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <Card sx={{ borderRadius: 3, borderLeft: '6px solid #00bcd4' }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 800 }}>{lexicon?.word || decodedWord}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" mt={1.5}>
                      <Typography color="text.secondary">/{lexicon?.pronunciation || 'ˈnjuː.ɑːns'}/</Typography>
                      <Button startIcon={<PlayArrowRoundedIcon />} size="small" onClick={handlePlayAudio}>Play</Button>
                    </Stack>
                  </Box>
                  <Box sx={{ minWidth: { md: 220 } }}>
                    <Typography sx={{ color: '#6b7280', letterSpacing: '0.08em', fontSize: 12, mb: 1 }}>TRANSLATION</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{translations[0] || '细微差别'}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography sx={{ color: '#0891b2', letterSpacing: '0.08em', fontSize: 12, mb: 1.5 }}>PRIMARY DEFINITION</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
                  {primaryDefinition ? `${primaryDefinition.part_of_speech} · ${primaryDefinition.definition}` : 'Definition unavailable.'}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
                  {(translations.length ? translations : ['meaning', 'context', 'usage']).map((item) => (
                    <Chip key={item} label={item} sx={{ bgcolor: '#ecfeff', color: '#0f766e' }} />
                  ))}
                </Stack>
                <Stack spacing={1.5}>
                  {examples.slice(0, 3).map((example, index) => (
                    <Box key={`${example.sentence}-${index}`} sx={{ p: 2, borderRadius: 2.5, bgcolor: '#f8fafc' }}>
                      <Typography sx={{ color: '#111827' }}>{example.sentence}</Typography>
                      {example.translation && <Typography color="text.secondary">{example.translation}</Typography>}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} mb={2.5}>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>Context-Aware Usage</Typography>
                  <Chip label="MASTERED" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700 }} />
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                  {displayedContexts.map((item, index) => {
                    const scene = SCENE_LABELS[item.scene] || { label: item.scene.toUpperCase(), color: '#1a73e8' };
                    return (
                      <Box key={`${item.scene}-${index}`} sx={{ p: 2.25, borderRadius: 2.5, borderLeft: `4px solid ${scene.color}`, bgcolor: '#f8fafc' }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: scene.color, mb: 1 }}>{scene.label}</Typography>
                        <Typography sx={{ fontStyle: 'italic', color: '#111827' }}>{highlightWord(item.example, decodedWord)}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2.5 }}>Knowledge Graph</Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <svg width="100%" viewBox="0 0 360 300" role="img" aria-label="Knowledge graph">
                    {graphLayout.map((node) => (
                      <line key={`edge-${node.word}`} x1={180} y1={150} x2={node.x} y2={node.y} stroke="#cbd5e1" strokeWidth="2" />
                    ))}
                    <circle cx="180" cy="150" r="42" fill="#111827" />
                    <text x="180" y="156" textAnchor="middle" fill="#fff" fontSize="16" fontWeight="700">{decodedWord}</text>
                    {graphLayout.map((node) => (
                      <g key={node.word}>
                        <circle cx={node.x} cy={node.y} r="28" fill={NODE_COLORS[node.type] || '#64748b'} />
                        <text x={node.x} y={node.y - 34} textAnchor="middle" fill="#334155" fontSize="12" fontWeight="700">{node.word}</text>
                      </g>
                    ))}
                  </svg>
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </Stack>

      <Stack spacing={3} sx={{ flex: 1, width: '100%' }}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3.5 }}>
            <Typography sx={{ color: '#6b7280', letterSpacing: '0.08em', fontSize: 12, mb: 2 }}>RELATED CONCEPTS</Typography>
            <Stack divider={<Divider flexItem />}>
              {(relatedConcepts.length ? relatedConcepts : graphNodes.slice(0, 5)).map((node: VocabGraphNode) => (
                <Button
                  key={`${node.type}-${node.word}`}
                  onClick={() => navigate(`/word/${encodeURIComponent(node.word)}?lang=${lang}`)}
                  sx={{ justifyContent: 'space-between', color: '#111827', px: 0, py: 1.5, fontWeight: 700 }}
                  endIcon={<ChevronRightRoundedIcon />}
                >
                  {node.word}
                </Button>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3.5 }}>
            <Typography sx={{ color: '#6b7280', letterSpacing: '0.08em', fontSize: 12, mb: 2 }}>LITERARY EXAMPLES</Typography>
            <Stack spacing={2.5}>
              {examples.slice(0, 2).map((example, index) => (
                <Box key={`${example.sentence}-${index}`}>
                  <Typography sx={{ fontStyle: 'italic', color: '#111827', mb: 0.75 }}>
                    “{highlightWord(example.sentence, decodedWord)}”
                  </Typography>
                  <Typography color="text.secondary">{example.source || example.author || 'DictZoom Literary Archive'}</Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, bgcolor: '#111827', color: '#fff' }}>
          <CardContent sx={{ p: 3.5 }}>
            <Stack direction="row" spacing={1.2} alignItems="center" mb={2}>
              <ShieldRoundedIcon />
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Your Mastery</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={72} sx={{ height: 10, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.16)', '& .MuiLinearProgress-bar': { bgcolor: '#22c55e' } }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', mt: 2 }}>
              You&apos;ve encountered this word 18 times across 6 different contexts. Keep it up!
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
}
